import { motion } from 'framer-motion';
import { memo, useRef, useEffect } from 'react';
import { NOTIFICATION_DURATION } from '../constants';
import { TNotificationEntry } from '../NotificationContextProvider';
import { css } from '@bts-web/utils-style-engine';
import {
  RegularCirclePlaceholder,
  RegularInfo,
  RegularSuccess,
  RegularWarning,
  RegularClose,
  RegularDismissCircle,
} from '../../common/svgIcons';

const getNotificationStyles = (visual: string, type: string) =>
  css({
    pointerEvents: 'auto',
    borderRadius: { base: type === 'global' ? 0 : '8px', lg: '8px' },
    boxShadow: {
      base: type === 'global' ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)',
      lg: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: 'small',
    gap: 'extra_small',
    position: 'relative',
    overflow: 'hidden',

    ...(visual === 'info' && { background: 'information.fill_secondary' }),
    ...(visual === 'error' && { background: 'negative.fill_secondary' }),
    ...(visual === 'warning' && { background: 'warning.fill_secondary' }),
    ...(visual === 'success' && { background: 'positive.fill_secondary' }),
    ...(visual === 'neutral' && { background: 'neutrals.fill_secondary' }),
  });

export const NotificationItem = memo<{
  notification: TNotificationEntry;
  onRemove: (id: string) => void;
}>(({ notification, onRemove }) => {
  const {
    id,
    title,
    subtitle,
    action,
    withClose,
    visual = 'info',
    type = 'toast',
    customDuration = NOTIFICATION_DURATION,
  } = notification;

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (customDuration !== 'infinite' && customDuration > 0) {
      timerRef.current = setTimeout(() => {
        onRemove(id);
      }, customDuration);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 350, scale: 0.3 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      whileHover={{ scale: 1.02 }}
      className={getNotificationStyles(visual, type)}
    >
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
        })}
      >
        <div
          className={css({
            display: 'flex',
            gap: 'small',
          })}
        >
          <motion.div
            initial={{ rotate: -180, scale: 0 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            style={{ display: 'flex' }}
          >
            {visual === 'success' && (
              <RegularSuccess size={20} color="#16764D" />
            )}

            {visual === 'warning' && (
              <RegularWarning size={20} color="#995917" />
            )}

            {visual === 'info' && <RegularInfo size={20} color="#1A4380" />}

            {visual === 'error' && (
              <RegularDismissCircle size={20} color="#CC3E3E" />
            )}

            {visual === 'neutral' && <RegularCirclePlaceholder size={20} />}
          </motion.div>

          <span
            className={css({
              fontSize: 'label.medium_bold',
              fontWeight: 'label.medium_bold',
              lineHeight: 'label.medium_bold',
              letterSpacing: 'label.medium_bold',
              marginTop: '-1px',
            })}
          >
            {title || 'Notification'}
          </span>
        </div>

        {withClose && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onRemove(id)}
            aria-label="Close notification"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
            }}
          >
            <RegularClose size={12.5} />
          </motion.button>
        )}
      </div>

      {subtitle && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ delay: 0.2 }}
          style={{ paddingInlineStart: '38px', whiteSpace: 'pre-wrap' }}
        >
          <p className={css({ fontSize: 'body.medium' })}>{subtitle}</p>
        </motion.div>
      )}

      {action && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ delay: 0.25 }}
          className={css({
            paddingInlineStart: '38px',
            fontSize: 'body.medium',
          })}
        >
          {action.component}
        </motion.div>
      )}

      {/* Progress bar - only show for non-infinite durations */}
      {customDuration !== 'infinite' && customDuration > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            insetInline: 0,
            height: '3px',
            background: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          <motion.div
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{
              duration: customDuration / 1000,
              ease: 'linear',
            }}
            style={{
              height: '100%',
              background: 'rgba(255, 255, 255, 0.7)',
            }}
          />
        </div>
      )}
    </motion.div>
  );
});

NotificationItem.displayName = 'NotificationItem';
